import { strFromU8, Unzip, UnzipInflate } from 'fflate';
import { z } from 'zod';

const MANIFEST_FILE = 'manifest.json';
const CHUNK_BYTES = 1024 * 1024;

const manifestSchema = z.object({ brandName: z.string().trim().min(1).max(120) });

/**
 * The manifest's bytes, read a chunk at a time until it is found. An export writes it first, so
 * a package of any size is read only that far; an older one is read through to its manifest.
 */
async function readManifest(file: File): Promise<Uint8Array | null> {
  const read: { manifest: Uint8Array | null } = { manifest: null };
  const chunks: Uint8Array[] = [];
  const unzip = new Unzip((entry) => {
    if (entry.name !== MANIFEST_FILE) return;
    entry.ondata = (error, data, final) => {
      if (error) throw error;
      chunks.push(data);
      if (final) {
        const manifest = new Uint8Array(chunks.reduce((size, chunk) => size + chunk.length, 0));
        chunks.reduce((offset, chunk) => {
          manifest.set(chunk, offset);
          return offset + chunk.length;
        }, 0);
        read.manifest = manifest;
      }
    };
    entry.start();
  });
  unzip.register(UnzipInflate);
  for (let offset = 0; offset < file.size && !read.manifest; offset += CHUNK_BYTES) {
    const end = Math.min(offset + CHUNK_BYTES, file.size);
    unzip.push(new Uint8Array(await file.slice(offset, end).arrayBuffer()), end === file.size);
  }
  return read.manifest;
}

/** The brand name a package's manifest gives, or null when the file is not a readable package. */
export async function readBrandPackageName(file: File): Promise<string | null> {
  try {
    const manifest = await readManifest(file);
    if (!manifest) return null;
    const parsed = manifestSchema.safeParse(JSON.parse(strFromU8(manifest)));
    return parsed.success ? parsed.data.brandName : null;
  } catch {
    return null;
  }
}

/** A slug the brand form accepts, made from a name. */
export const slugFromName = (name: string): string =>
  name
    .toLowerCase()
    .normalize('NFKD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9_-]+/g, '-')
    .replace(/^[-_]+|-+$/g, '')
    .slice(0, 64);
